/* =========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* =========================================================================== */

/*
 * TagFromCampaign.jsx — สร้างป้ายราคาชุดจากแคมเปญด้วยคลิกเดียว   (งานข้อ 5 · Step 7.6)
 *   ค้นหาแคมเปญ → เลือกสาขาและแม่แบบ → OPTIONS /tag/from_campaign → เปิดป้ายที่สร้าง
 *   ของเดิมพนักงานต้องเพิ่มสินค้าทีละตัวแล้วเปิดแก้ทีละตัวเพื่อใส่ขั้นราคาแคมเปญ
 *   ต่อเข้า Tag.jsx แบบส่ง props ไม่ดึง redux เอง เพื่อให้ไฟล์นี้ทดสอบแยกได้
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';

import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

/* "qty=price=type" (รูปแบบ tags_list.line*) → "3 ชิ้น 360 บาท" — ค่าจากฐานเป็น 1.000000 / 139.00 อ่านยาก (6 ต.ค. 2569) */
const lineText = (line) => {
  const [qty, price] = String(line || '').split('=');
  const n = (v) => Number(v || 0).toLocaleString('th-TH', { maximumFractionDigits: 2 });
  return `${n(qty)} ชิ้น ${n(price)} บาท`;
};

const ERROR_TEXT = {
  campaign_all_products_unsupported:
    'แคมเปญนี้ตั้งเป็น "สินค้าทุกตัวเข้าร่วม" จึงไม่มีรายการสินค้าให้ทำป้าย ต้องเลือกสินค้าเองในป้าย',
  campaign_not_in_branch: 'แคมเปญนี้ไม่ได้ใช้กับสาขาที่เลือก',
  campaign_no_products: 'แคมเปญนี้ยังไม่มีสินค้าที่ร่วมรายการ',
  campaign_too_many_products: 'สินค้าเกิน 200 รายการ แบ่งแคมเปญหรือเลือกสินค้าเองในป้าย',
  campaign_type_unsupported: 'ป้ายรองรับเฉพาะโปรโมชัน "ซื้อครบจำนวนลดราคา" และ "ยิ่งซื้อยิ่งลด"',
  campaign_no_tier: 'แคมเปญนี้ยังไม่ได้ตั้งจำนวนชิ้นของขั้นราคา',
  campaign_not_found: 'ไม่พบแคมเปญ',
  campaign_branch_required: 'เลือกแคมเปญและสาขาก่อน',
  campaign_expired: 'แคมเปญนี้หมดอายุแล้ว ทำป้ายไม่ได้',
  campaign_not_started: 'แคมเปญนี้ยังไม่เริ่ม ทำป้ายไม่ได้',
};

/* เหตุผลสั้นบนแถวที่เลือกไม่ได้ (ข้อความเต็มอยู่ใน title) · เดิมขึ้น "ยังไม่รองรับ" ทุกเหตุผล (18 ต.ค. 2569) */
const SHORT_TEXT = {
  campaign_type_unsupported: 'ประเภทโปรโมชันนี้ยังไม่รองรับ',
  campaign_no_tier: 'ยังไม่ตั้งขั้นราคา',
  campaign_all_products_unsupported: 'ใช้กับทุกสินค้า',
  campaign_not_in_branch: 'ไม่ใช้กับสาขานี้',
};

/* ชื่อชนิดโปรเดียวกับหน้าแคมเปญ (Campaign.jsx promotionDisplay) แทนตัวอักษร A–E */
const PROMO_NAME = {
  A: 'ซื้อครบจำนวนลดราคา',
  B: 'ซื้อครบมูลค่าได้ของแถม',
  C: 'ซื้อครบจำนวนได้ของแถม',
  D: 'ยิ่งซื้อยิ่งลด',
  E: 'ซื้อครบมูลค่าได้ส่วนลด',
};

/* นอกช่วงวันที่ → เหตุผลพร้อมวันที่ (campaign_status มาจาก campaign.con.php view) */
const periodText = (c) => {
  if (c.check_date === 'Y') return '';
  if (c.campaign_status === 'SCHEDULED')
    return `ยังไม่เริ่ม เริ่ม ${Func.dateDisplay(c.date_start)}`;
  if (c.campaign_status === 'EXPIRED') return `หมดอายุแล้ว ${Func.dateDisplay(c.date_stop)}`;
  return 'อยู่นอกช่วงวันที่';
};
const statusLabel = (c) =>
  c.check_date === 'Y'
    ? 'ใช้อยู่'
    : c.campaign_status === 'SCHEDULED'
    ? 'ยังไม่เริ่ม'
    : c.campaign_status === 'EXPIRED'
    ? 'หมดอายุแล้ว'
    : 'นอกช่วงวันที่';

function TagFromCampaign({ open, onClose, app, branches, templates, onCreated, onError }) {
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [branchId, setBranchId] = useState('');
  const [templateId, setTemplateId] = useState('0');
  const [busy, setBusy] = useState(false);
  const [searchState, setSearchState] = useState('idle');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewRetry, setPreviewRetry] = useState(0);
  const requestId = useRef(0);
  /* มติประชุมลูกค้า (5 ต.ค. 2569) — แคมเปญนอกช่วงวันที่ทำป้ายไม่ได้ เว้นแต่มีสิทธิ์ campaign_expired และติ๊กยืนยัน */
  const [includeExpired, setIncludeExpired] = useState(false);
  const canOverride = Boolean(app.perm && app.perm.campaign_expired);
  /* สาขาเริ่มต้น = สาขาประจำของผู้ใช้ (login user.branch_id ← users.office_id) ถ้าอยู่ในรายการ · เปลี่ยนเองได้
     เจ้าของงานให้ตัดสินใจแทน 6 ต.ค. 2569 — พนักงานส่วนใหญ่ทำป้ายให้สาขาตัวเอง ถ้าแคมเปญไม่ใช้กับสาขานี้จะขึ้นเหตุผลให้เปลี่ยน */
  const userBranch = String((app.user && app.user.branch_id) || '');
  const defaultBranch = (branches || []).some((b) => String(b.id) === userBranch) ? userBranch : '';
  /* ชุดใหม่เริ่มเป็นป้ายโปร 7×5 เหมือนปุ่ม + (ตรวจการใช้งาน 1.4 · 1 พ.ย. 2569) */
  const promo = (templates || []).find((x) => x.code === 'PROMO_7X5_A4' && x.layout === 'PROMO');
  const defaultTemplate = promo ? String(promo.id) : '0';

  useEffect(() => {
    requestId.current += 1;
    setSearch('');
    setRows([]);
    setSelected(null);
    setIncludeExpired(false);
    setSearchState('idle');
    setTotal(0);
    setPage(1);
    setBranchId(defaultBranch);
    setTemplateId(defaultTemplate);
  }, [open, defaultBranch, defaultTemplate]);

  const blockCode = (c) => {
    if (!['A', 'D'].includes(c.promotion_type)) return 'campaign_type_unsupported';
    if (Number(c.quantity) <= 0) return 'campaign_no_tier';
    if (c.is_all_products === 'Y') return 'campaign_all_products_unsupported';
    if (
      branchId &&
      c.all_branch !== 'Y' &&
      !(c.branches || []).some((b) => String(b.id) === String(branchId))
    )
      return 'campaign_not_in_branch';
    return '';
  };
  const unsupported = (c) => ERROR_TEXT[blockCode(c)] || '';
  const find = (nextPage = 1) => {
    requestId.current += 1;
    const req = requestId.current;
    setSearchState('loading');
    setSelected(null);
    setIncludeExpired(false);
    setPreview(null);
    FetchData('POST', '/campaign', app.token, {
      lang: app.lang,
      search,
      type: 'ALL',
      enable: 'Y',
      expire: 'ALL',
      page: nextPage,
      per_page: 8,
      orderby: 'tb.id',
      ordersort: 'desc',
    })
      .then((result) => {
        if (req !== requestId.current) return;
        setRows(result.status === 'ok' ? result.data || [] : []);
        setTotal(Number(result.total || 0));
        setPage(nextPage);
        setSearchState(result.status === 'ok' ? 'done' : 'error');
      })
      .catch(() => {
        if (req === requestId.current) {
          setRows([]);
          setSearchState('error');
        }
      });
  };

  useEffect(() => {
    let alive = true;
    setPreview(null);
    setPreviewError('');
    setPreviewBusy(false);
    if (
      !open ||
      !selected ||
      !branchId ||
      unsupported(selected) ||
      (selected.check_date !== 'Y' && !(canOverride && includeExpired))
    )
      return undefined;
    setPreviewBusy(true);
    FetchData('OPTIONS', '/tag/preview_campaign', app.token, {
      campaign_id: selected.id,
      branch_id: branchId,
      include_expired: canOverride && includeExpired ? 'Y' : 'N',
      _t: Date.now(),
    })
      .then((result) => {
        if (!alive) return;
        if (result.status === 'ok') setPreview(result);
        else setPreviewError(ERROR_TEXT[result.msg] || 'ตรวจป้ายไม่สำเร็จ ลองอีกครั้ง');
        setPreviewBusy(false);
      })
      .catch(() => {
        if (alive) {
          setPreviewBusy(false);
          setPreviewError('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง');
        }
      });
    return () => {
      alive = false;
    };
  }, [open, selected, branchId, includeExpired, previewRetry]);

  /* นอกช่วงวันที่: บอกเหตุผล+วันที่บรรทัดเดียว ไม่พูดถึงสิทธิ์ที่ผู้ใช้ไม่มี · ผู้มีสิทธิ์เห็นช่องติ๊กยืนยันด้านบน (18 ต.ค. 2569) */
  const periodBlocked =
    Boolean(selected) && selected.check_date !== 'Y' && !(canOverride && includeExpired);
  const reason = !branchId
    ? 'เลือกสาขาก่อนสร้างป้าย'
    : !selected
    ? 'เลือกแคมเปญที่จะทำป้าย'
    : unsupported(selected) ||
      (periodBlocked ? periodText(selected) : '') ||
      (previewBusy ? 'กำลังตรวจจำนวนและราคาของสาขา…' : previewError) ||
      (!preview ||
      String(preview.campaign_id) !== String(selected.id) ||
      String(preview.branch_id) !== String(branchId)
        ? 'รอตรวจความพร้อมของป้าย'
        : '');

  const create = () => {
    if (busy || reason) {
      onError(ERROR_TEXT.campaign_branch_required);
      return;
    }
    setBusy(true);
    FetchData('OPTIONS', '/tag/from_campaign', app.token, {
      campaign_id: selected.id,
      branch_id: branchId,
      template_id: templateId,
      include_expired: canOverride && includeExpired ? 'Y' : 'N',
      _t: Date.now(),
    })
      .then((result) => {
        setBusy(false);
        if (result.status === 'ok') {
          onCreated(result);
        } else {
          onError(ERROR_TEXT[result.msg] || 'สร้างป้ายไม่สำเร็จ ลองอีกครั้ง');
        }
      })
      .catch(() => {
        /* เน็ตหลุด/เซิร์ฟเวอร์ไม่ตอบ — ปลดล็อกหน้าต่างให้กดใหม่ได้ (16 ต.ค. 2569) */
        setBusy(false);
        onError('เชื่อมต่อไม่สำเร็จ ลองอีกครั้ง');
      });
  };

  /* ราคาแบบ "1=59/2=100" ที่ทางร้านคุ้น — quantity/price ของแคมเปญคือขั้นที่ 2 */
  const dealText = (c) =>
    Number(c.quantity) > 0
      ? `${Func.numberDisplay(c.quantity, 0)} = ${Func.numberDisplay(c.price, 0)}`
      : '-';

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth='md'>
      <DialogTitle>สร้างป้ายจากแคมเปญ</DialogTitle>
      <DialogContent>
        <MDBox mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <MDInput
                value={search}
                onChange={(e) => {
                  requestId.current += 1;
                  setSearch(e.target.value);
                  setRows([]);
                  setSelected(null);
                  setIncludeExpired(false);
                  setSearchState('idle');
                  setTotal(0);
                }}
                onKeyDown={(e) => e.key === 'Enter' && find()}
                variant='standard'
                fullWidth
                placeholder='รหัสหรือชื่อแคมเปญ หรือรหัสสินค้า'
                InputProps={{
                  startAdornment: <InputAdornment position='start'>ค้นหาแคมเปญ :</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDButton
                variant='outlined'
                color='dark'
                fullWidth
                onClick={() => find()}
                disabled={busy || searchState === 'loading'}
              >
                <Icon>search</Icon>&nbsp;ค้นหา
              </MDButton>
            </Grid>
          </Grid>
        </MDBox>

        <MDBox
          mt={2}
          sx={{
            maxHeight: selected ? 170 : 280,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <Table size='small'>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell>
                    <MDTypography variant='caption' color='text'>
                      {searchState === 'loading'
                        ? 'กำลังค้นหา…'
                        : searchState === 'error'
                        ? 'โหลดไม่สำเร็จ กดค้นหาเพื่อลองอีกครั้ง'
                        : searchState === 'done'
                        ? 'ไม่พบแคมเปญ ลองเปลี่ยนคำค้น'
                        : 'พิมพ์คำค้นแล้วกดค้นหา'}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  selected={selected && selected.id === c.id}
                  onClick={() => {
                    if (!busy && !unsupported(c) && searchState !== 'loading') {
                      setSelected(c);
                      setIncludeExpired(false);
                    }
                  }}
                  /* เลือกด้วยคีย์บอร์ดได้ — Tab ไปที่แถวแล้ว Enter/Space (18 ต.ค. 2569) */
                  role='button'
                  tabIndex={unsupported(c) ? -1 : 0}
                  aria-pressed={Boolean(selected && selected.id === c.id)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    if (!busy && !unsupported(c) && searchState !== 'loading') {
                      setSelected(c);
                      setIncludeExpired(false);
                    }
                  }}
                  aria-disabled={Boolean(unsupported(c))}
                  title={unsupported(c) || undefined}
                  sx={{
                    cursor: unsupported(c) ? 'not-allowed' : 'pointer',
                    opacity: unsupported(c) ? 0.55 : 1,
                  }}
                >
                  <TableCell sx={{ width: 110 }}>
                    <MDTypography variant='caption' fontWeight='bold'>
                      {c.code}
                    </MDTypography>
                  </TableCell>
                  <TableCell>
                    <MDTypography variant='caption'>{c.name}</MDTypography>
                    {unsupported(c) && (
                      <MDTypography variant='caption' color='error' display='block'>
                        {SHORT_TEXT[blockCode(c)]}
                      </MDTypography>
                    )}
                  </TableCell>
                  <TableCell sx={{ width: 120 }}>
                    <MDTypography variant='caption' fontWeight='bold' color='error'>
                      {unsupported(c) ? '' : dealText(c)}
                    </MDTypography>
                  </TableCell>
                  <TableCell sx={{ width: 170 }}>
                    <Chip
                      size='small'
                      label={statusLabel(c)}
                      color={c.check_date === 'Y' ? 'success' : 'default'}
                      variant='outlined'
                    />
                    <MDTypography variant='caption' color='text' display='block'>
                      {PROMO_NAME[c.promotion_type] || c.promotion_type}
                    </MDTypography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </MDBox>

        {total > 0 && (
          <MDBox display='flex' alignItems='center' gap={1} mt={1}>
            <MDTypography variant='caption'>
              พบ {total} รายการ · หน้า {page}/{Math.max(1, Math.ceil(total / 8))}
            </MDTypography>
            <MDButton
              size='small'
              disabled={page <= 1 || searchState === 'loading'}
              onClick={() => find(page - 1)}
            >
              ก่อนหน้า
            </MDButton>
            <MDButton
              size='small'
              disabled={page * 8 >= total || searchState === 'loading'}
              onClick={() => find(page + 1)}
            >
              ถัดไป
            </MDButton>
          </MDBox>
        )}

        <MDBox mt={3}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MDSelect
                label='สาขา'
                labelDefault='เลือกสาขา'
                data={branches}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MDSelect
                label='แม่แบบพิมพ์'
                labelDefault='ตามค่าตั้งต้น (A4 แนวนอน 16 ดวง)'
                valueDefault='0'
                data={templates}
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              />
            </Grid>
          </Grid>
        </MDBox>

        {selected ? (
          <MDBox mt={2}>
            <MDTypography variant='button' fontWeight='bold'>
              {selected.code} · {selected.name}
            </MDTypography>
            {selected.check_date === 'Y' ? (
              <MDTypography variant='caption' color='text' display='block'>
                ใช้ถึง {Func.dateDisplay(selected.date_stop)}
              </MDTypography>
            ) : canOverride ? (
              <MDBox mt={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeExpired}
                      onChange={(e) => setIncludeExpired(e.target.checked)}
                    />
                  }
                  label='ยืนยันทำป้ายแม้แคมเปญอยู่นอกช่วงวันที่'
                />
              </MDBox>
            ) : null}
          </MDBox>
        ) : null}
        <MDBox mt={2} aria-live='polite'>
          {reason && (
            <MDTypography
              variant='caption'
              color={
                previewError || periodBlocked || (selected && unsupported(selected))
                  ? 'error'
                  : 'text'
              }
            >
              {reason}
            </MDTypography>
          )}
          {previewError && (
            <MDButton size='small' onClick={() => setPreviewRetry((v) => v + 1)}>
              ตรวจอีกครั้ง
            </MDButton>
          )}
          {!reason && preview && (
            <>
              <MDTypography variant='button' fontWeight='bold'>
                พร้อมสร้าง {preview.count} ป้าย · โปรโมชันสาขานี้ {lineText(preview.line2)}
              </MDTypography>
              {/* แจ้งเฉพาะเมื่อมี — สินค้าที่ไม่ได้ป้าย และราคาปกติ 0 บาท (16 ต.ค. 2569) */}
              {Number(preview.skipped) > 0 && (
                <MDTypography variant='caption' color='error' display='block'>
                  ข้าม {preview.skipped} รายการ (ไม่มีบาร์โค้ด/ปิดขาย)
                </MDTypography>
              )}
              {Number(preview.zero_price) > 0 && (
                <MDTypography variant='caption' color='error' display='block'>
                  ราคาปกติ 0 บาท {preview.zero_price} รายการ
                </MDTypography>
              )}
              <MDBox sx={{ maxHeight: 160, overflow: 'auto' }}>
                {preview.products.map((p) => (
                  <MDTypography
                    key={`${p.product_id}-${p.line1}`}
                    display='block'
                    variant='caption'
                    color={p.zero_price === 'Y' ? 'error' : 'dark'}
                  >
                    {p.sku} · {p.name} · ราคาปกติ {lineText(p.line1)}
                  </MDTypography>
                ))}
              </MDBox>
            </>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant='text' color='error' onClick={onClose} disabled={busy}>
          ยกเลิก
        </MDButton>
        <MDButton
          variant='gradient'
          color='info'
          onClick={create}
          disabled={busy || Boolean(reason)}
        >
          <Icon>post_add</Icon>&nbsp;สร้างป้าย
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

TagFromCampaign.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  app: PropTypes.shape({
    token: PropTypes.string,
    lang: PropTypes.string,
    perm: PropTypes.objectOf(PropTypes.bool),
    user: PropTypes.shape({
      branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  branches: PropTypes.arrayOf(PropTypes.object).isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  onCreated: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default TagFromCampaign;
